import AuthContext from "common/store/AuthContext";

import { InquiryData } from "pages/inquiry/types/InquiryInfo";
import { ReportData } from "../types/ReportData";
import { ApiReportData } from "../types/ApiReportData";
import { useCallback, useContext } from "react";
import { generateErrorMsg, getServerUrl } from "utils/commonTools";
import axios from "axios";
import { GetBasicResponse } from "common/types/responses/BasicResponse";
import { safeApiDate, toJapaneseDate } from "utils/DateTools";
import { MapArea } from "common/types/MapArea";
import { ChartOverlayData } from "common/components/map/common/osm/components/ChartOverlayDrawer";
import { PinData } from "common/components/map/common/osm/components/PinDrawer";
import { useMapOverlay } from "./useMapOverlay";

export const useMapInquiry = () => {
  const authCtxt = useContext(AuthContext);
  const { token } = authCtxt;
  const { addToOverlay } = useMapOverlay();

  const searchData = useCallback(
    async (
      parkId: number,
      from: Date,
      to: Date,
    ): Promise<{ succeeded: boolean; msg?: string; data?: ReportData<InquiryData>[] }> => {
      const config = {
        headers: { "Content-type": "application/json", Authorization: `Bearer ${token}` },
      };
      try {
        // 日報データの取得
        const url =
          getServerUrl() +
          `report/${parkId}/inquiries?from=${toJapaneseDate(from)}&to=${toJapaneseDate(to)}`;

        const { data } = await axios.get(url, config);
        const reportData = data as GetBasicResponse<ApiReportData<InquiryData>[]>;
        if (!reportData) {
          return { succeeded: false, msg: "取得したデータに異常がありました" };
        }

        const normalized = (reportData.data ?? []).map((item) => ({
          ...item,
          date: safeApiDate(item.date),
        }));

        authCtxt.updateToken(reportData.token, new Date(reportData.expiresAt));

        return {
          succeeded: true,
          msg: "",
          data: normalized,
        };
      } catch (error) {
        var errMsg = generateErrorMsg(error);
        return { succeeded: false, msg: errMsg };
      }
    },
    [authCtxt, token],
  );

  const convertToOverlays = (
    data: ReportData<InquiryData>[],
    metaAreas: MapArea[],
  ): ChartOverlayData[] => {
    const overlayMap = new Map<number, ChartOverlayData>();

    for (const item of data) {
      const category = item.value.category;
      if (!category) continue;
      const count = item.value.numIncident;

      // エリアで指定（複数あり）
      if (item.value.areas && item.value.areas.length > 0) {
        item.value.areas.forEach((area) => {
          addToOverlay(overlayMap, area, category, count, metaAreas);
        });
      }
      // ポイントで指定
      else if (item.value.point) {
        const area = item.value.point.area;
        const pin: PinData = { point: item.value.point.coords, label: item.value.point.title };
        if (area) addToOverlay(overlayMap, area, category, count, metaAreas, pin);
        else
          addToOverlay(overlayMap, { id: -2, name: "位置のみ" }, category, count, metaAreas, pin);
      }
      // 指定なし
      else addToOverlay(overlayMap, { id: -1, name: "地区なし" }, category, count, metaAreas);
    }

    overlayMap.forEach((x) => x.bars.sort((a, b) => (a.label.id < b.label.id ? -1 : 1)));
    return Array.from(overlayMap.values());
  };

  return { searchData, convertToOverlays };
};
